/**
 * Excel Editor Orchestrator — Zod Schemas
 *
 * Canonical schema location for orchestrator and assign-task payloads.
 */
import { z } from "zod";
export const ExcelEditorRequirementSchema = z.object({
    id: z.string().describe("Unique requirement identifier (e.g. 'A', 'B')"),
    requirement: z.string().describe("The editing requirement or task"),
});
export const ExcelWorkbookContextSnapshotSchema = z.object({
    workbookName: z.string(),
    activeWorksheet: z.string(),
    selectedRange: z.string(),
    worksheetCount: z.number(),
    worksheetNames: z.array(z.string()).optional(),
    usedRange: z.string().optional(),
});
export const LiveExcelOrchestratorInputSchema = z.object({
    message: z.string().describe("The user's message / request"),
    requirements: z
        .array(ExcelEditorRequirementSchema)
        .default([])
        .describe("Parsed requirements from the message"),
    workbookContext: ExcelWorkbookContextSnapshotSchema.describe("Current workbook state snapshot"),
});
export const ExcelTaskInstructionsContextSchema = z.object({
    detailedInstructions: z.string().describe("Natural-language task description"),
    alphasenseContext: z.array(z.unknown()).optional(),
    structuredDataContext: z.array(z.unknown()).optional(),
    externalFileContext: z
        .array(z.object({
        pageId: z.string(),
        purpose: z.string().optional(),
    }))
        .optional(),
    headersContext: z
        .array(z.object({
        sheet: z.string(),
        summary: z.string(),
        references: z.array(z.string()).optional(),
    }))
        .optional(),
    rangesContext: z
        .array(z.object({
        range: z.string(),
        summary: z.string(),
        references: z.array(z.string()).optional(),
    }))
        .optional(),
    cellsContext: z
        .array(z.object({
        cells: z.array(z.string()),
        summary: z.string(),
        references: z.array(z.string()).optional(),
    }))
        .optional(),
    formatRangesContext: z
        .array(z.object({
        range: z.string(),
        summary: z.string(),
        formatNotes: z.array(z.string()).optional(),
    }))
        .optional(),
    formatSheetContext: z
        .array(z.object({
        sheet: z.string(),
        summary: z.string(),
        formatReferences: z.array(z.string()).optional(),
    }))
        .optional(),
    valuesContext: z
        .array(z.object({
        range: z.string(),
        summary: z.string(),
        chartReferences: z.array(z.string()).optional(),
    }))
        .optional(),
    chartsContext: z
        .array(z.object({
        chartSheetAndName: z.string(),
        summary: z.string(),
        chartReferences: z.array(z.string()).optional(),
    }))
        .optional(),
});
export const AssignEditTaskSchema = z.object({
    taskType: z.literal("edit"),
    targetRange: z.string().describe("A1-notation range being edited"),
    ruleSet: z.enum(["financial", "generalist"]).describe("Domain rule set"),
    condensedInstructionsForUi: z.string().describe("Short summary for UI display"),
    taskInstructions: ExcelTaskInstructionsContextSchema,
    useSummary: z.boolean().optional(),
});
export const AssignFormatTaskSchema = z.object({
    taskType: z.literal("format"),
    targetRange: z.string().describe("A1-notation range being formatted"),
    ruleSet: z.enum(["financial", "generalist"]).describe("Domain rule set"),
    condensedInstructionsForUi: z.string().describe("Short summary for UI display"),
    taskInstructions: ExcelTaskInstructionsContextSchema,
    useSummary: z.boolean().optional(),
});
export const AssignChartTaskSchema = z.object({
    taskType: z.literal("chart"),
    ruleSet: z.enum(["financial", "generalist"]).describe("Domain rule set"),
    condensedInstructionsForUi: z.string().describe("Short summary for UI display"),
    targetChart: z.object({
        stylingExistingChart: z.boolean(),
        chartDataRange: z.string().optional(),
        chartName: z.string().optional(),
        specialChartType: z.enum(["waterfall", "football_field"]).optional(),
    }),
    taskInstructions: ExcelTaskInstructionsContextSchema,
});
export const AssignQuestionTaskSchema = z.object({
    detailedInstructions: z.string().describe("The question to answer"),
});
